
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CardDescription } from "@/components/ui/card";
import { usePasswordManagement } from "@/hooks/usePasswordManagement";
import { Check, X } from "lucide-react";

const SecuritySettings = () => {
  const {
    formData,
    isLoading,
    isOAuthOnly,
    validatePassword,
    setPassword,
    changePassword,
    updateFormData
  } = usePasswordManagement();

  const passwordValidation = validatePassword(formData.newPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isOAuthOnly) {
      await setPassword();
    } else {
      await changePassword();
    }
  };

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <X className="h-4 w-4 text-red-500" />
      )}
      <span className={met ? "text-green-700" : "text-red-700"}>{text}</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">
          {isOAuthOnly ? "Set Password" : "Change Password"}
        </h3>
        <CardDescription>
          {isOAuthOnly 
            ? "Set a password to enable email/password login in addition to your OAuth provider."
            : "Update your current password for enhanced security."
          }
        </CardDescription>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {!isOAuthOnly && (
          <div className="space-y-2">
            <Label htmlFor="current-password">Current Password</Label>
            <Input
              id="current-password"
              type="password"
              value={formData.currentPassword}
              onChange={(e) => updateFormData("currentPassword", e.target.value)}
              placeholder="Enter your current password"
              required
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="new-password">
            {isOAuthOnly ? "New Password" : "New Password"}
          </Label>
          <Input
            id="new-password"
            type="password"
            value={formData.newPassword}
            onChange={(e) => updateFormData("newPassword", e.target.value)}
            placeholder="Enter your new password"
            required
          />
          
          {formData.newPassword && (
            <div className="mt-3 p-3 bg-muted rounded-lg space-y-2">
              <p className="text-sm font-medium mb-2">Password Requirements:</p>
              <RequirementItem 
                met={passwordValidation.requirements.minLength} 
                text="At least 8 characters"
              />
              <RequirementItem 
                met={passwordValidation.requirements.hasUppercase} 
                text="One uppercase letter"
              />
              <RequirementItem 
                met={passwordValidation.requirements.hasLowercase} 
                text="One lowercase letter"
              />
              <RequirementItem 
                met={passwordValidation.requirements.hasNumber} 
                text="One number"
              />
              <RequirementItem 
                met={passwordValidation.requirements.hasSpecialChar} 
                text="One special character"
              />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm-password">Confirm New Password</Label>
          <Input
            id="confirm-password"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => updateFormData("confirmPassword", e.target.value)}
            placeholder="Confirm your new password"
            required
          />
          {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
            <p className="text-sm text-red-600">Passwords do not match</p>
          )}
        </div>

        <Button 
          type="submit" 
          disabled={isLoading || !passwordValidation.isValid || formData.newPassword !== formData.confirmPassword}
          className="w-full"
        >
          {isLoading 
            ? (isOAuthOnly ? "Setting Password..." : "Changing Password...") 
            : (isOAuthOnly ? "Set Password" : "Change Password")
          }
        </Button>
      </form>
    </div>
  );
};

export default SecuritySettings;
